import { ChevronDownIcon, PhoneIcon, Trash2Icon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { type Control, type FieldPath, useController, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import { Checkbox } from "../../../../../components/ui/checkbox";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "../../../../../components/ui/form";
import { Input } from "../../../../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { Textarea } from "../../../../../components/ui/textarea";
import { cn } from "../../../../../lib/utils";
import { type ApplicantSsnType, getIncomeTypeOptions, INCOME_ORDINAL_LABELS } from "../constants";
import { computeTaxes } from "../incomeCalculator/incomeCalculator";
import type { PayFrequency } from "../incomeCalculator/incomeCalculator.constants";
import { NET_PAY_FACTOR, PAY_FREQUENCY_FACTOR } from "../incomeCalculator/incomeCalculator.constants";
import type { IncomeBreakdownResult } from "../incomeCalculator/incomeCalculator.types";
import type { IncomeFieldConfig } from "../incomeFields.config";
import { INCOME_FIELDS } from "../incomeFields.config";
import { useFormReadOnly } from "../readOnlyContext";
import { getSurfacedSelect } from "../selectSurfacing";
import type { IncomeInput, SignersInput } from "../Signers.schema";
import { IncomeCalculatorPanel } from "./IncomeCalculatorPanel";
import { IncomeTaxesPanel } from "./IncomeTaxesPanel";
import { PullPurchasedEmployerInfo } from "./PullPurchasedEmployerInfo";
import { useZipAutofill } from "./useZipAutofill";
import { ZipLookupMessage } from "./ZipLookupMessage";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type PersonPath = "driver" | "secondSigner";

interface IncomeBlockProperties {
  control: Control<SignersInput>;
  personPath: PersonPath;
  index: number;
  onRemove?: () => void;
  /** Deal / approval id, threaded down for the Pull Purchased Employer Info flow. */
  approvalId?: string;
  /** Real applicant id for this person, threaded down for the Pull Purchased Employer Info flow. */
  applicantId?: string;
  /** Applicant SSN/ITIN status — filters the Income Type options. */
  applicantSsnType: ApplicantSsnType;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getFieldName(personPath: PersonPath, index: number, fieldName: keyof IncomeInput): FieldPath<SignersInput> {
  return `${personPath}.incomes.${index}.${fieldName}` as FieldPath<SignersInput>;
}

// ---------------------------------------------------------------------------
// SelectField — uses useFormField to wire aria props to SelectTrigger directly
// ---------------------------------------------------------------------------
interface SelectFieldInnerProperties {
  value: string;
  onValueChange: (v: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
  label: string;
  required?: boolean;
  className?: string;
  hidePlaceholder?: boolean;
}

function SelectFieldInner({
  value,
  onValueChange,
  options,
  label,
  required,
  className,
  hidePlaceholder,
}: SelectFieldInnerProperties) {
  const { t } = useTranslation();
  const { formItemId, formDescriptionId, formMessageId, error } = useFormField();
  // Radix Select ignores the enclosing disabled <fieldset>, so honour the lock
  // explicitly from the shared read-only context.
  const readOnly = useFormReadOnly();
  // Use shared surfacing helper: surfaces an out-of-options value and computes
  // the remount key so Radix Select can display it from the first render.
  const { resolvedOptions, selectKey } = getSurfacedSelect(value, options);
  return (
    <Select key={selectKey} value={value} onValueChange={onValueChange} disabled={readOnly}>
      <SelectTrigger
        id={formItemId}
        aria-describedby={error ? `${formDescriptionId} ${formMessageId}` : formDescriptionId}
        aria-invalid={error ? true : undefined}
        aria-label={label}
        className={className}
        disabled={readOnly}
      >
        <SelectValue placeholder={hidePlaceholder ? undefined : t("review.selectPlaceholder", { label })} />
        {required && <span className="sr-only">{t("review.required")}</span>}
      </SelectTrigger>
      <SelectContent>
        {resolvedOptions.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ---------------------------------------------------------------------------
// Individual field renderer
// ---------------------------------------------------------------------------
interface FieldRendererProperties {
  config: IncomeFieldConfig;
  control: Control<SignersInput>;
  personPath: PersonPath;
  index: number;
  /** Optional blur handler for text/number/date inputs (runs after RHF's onBlur). */
  onBlur?: () => void;
  /** Force the invalid (red-border) state independent of RHF validation — e.g. a failed ZIP lookup. */
  invalid?: boolean;
  /** Overrides a select config's static options (e.g. SSN/ITIN-filtered Income Type). */
  optionsOverride?: ReadonlyArray<{ value: string; label: string }>;
}

function FieldRenderer({
  config,
  control,
  personPath,
  index,
  onBlur,
  invalid,
  optionsOverride,
}: FieldRendererProperties) {
  const { t } = useTranslation();
  const fieldPath = getFieldName(personPath, index, config.name);
  const label = t(config.label);

  if (config.kind === "select") {
    return (
      <FormField
        control={control}
        name={fieldPath}
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {label}
              {config.required && (
                <span className="text-destructive ml-0.5" aria-hidden="true">
                  *
                </span>
              )}
            </FormLabel>
            <SelectFieldInner
              value={field.value == null ? "" : String(field.value)}
              onValueChange={field.onChange}
              options={optionsOverride ?? config.options}
              label={label}
              required={config.required}
              className={config.controlClassName}
              hidePlaceholder={config.hidePlaceholder}
            />
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  if (config.kind === "textarea") {
    return (
      <FormField
        control={control}
        name={fieldPath}
        render={({ field }) => (
          <FormItem className="col-span-full">
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <Textarea
                {...field}
                rows={1}
                className={config.controlClassName}
                value={field.value == null ? "" : String(field.value)}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  // text | phone | date | number
  const inputType = config.kind === "date" ? "date" : config.kind === "number" ? "number" : "text";

  return (
    <FormField
      control={control}
      name={fieldPath}
      render={({ field }) => (
        <FormItem className={config.itemClassName}>
          <FormLabel className={cn(config.hideLabel ? "sr-only" : config.labelClassName)}>
            {label}
            {config.required && (
              <span className="text-destructive ml-0.5" aria-hidden="true">
                *
              </span>
            )}
          </FormLabel>
          <FormControl>
            <Input
              {...field}
              type={inputType}
              // Force the red-border invalid state on a failed ZIP lookup. Spread
              // conditionally so other fields keep FormControl's RHF-driven
              // aria-invalid untouched.
              {...(invalid ? { "aria-invalid": true } : {})}
              placeholder={config.placeholder ? t(config.placeholder) : undefined}
              className={config.controlClassName}
              value={field.value == null ? "" : String(field.value)}
              // Number fields must store a number (or undefined when blank) to
              // satisfy the z.number() schema — RHF's default onChange would keep
              // the raw string and trip an "expected number" validation error.
              onChange={
                config.kind === "number"
                  ? (event) => {
                      const raw = event.target.value;
                      field.onChange(raw === "" ? undefined : Number(raw));
                    }
                  : field.onChange
              }
              onBlur={() => {
                field.onBlur();
                onBlur?.();
              }}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// PhoneExtensionField — Employment Verification Phone + Extension grouped together.
// The two inputs live in their own row so an error message (rendered outside
// that row) never shifts the extension. Label sits above the inputs below lg,
// and to the left (centered) at lg.
// ---------------------------------------------------------------------------
interface PhoneExtensionFieldProperties {
  control: Control<SignersInput>;
  personPath: PersonPath;
  index: number;
}

function PhoneExtensionField({ control, personPath, index }: PhoneExtensionFieldProperties) {
  const { t } = useTranslation();
  const phonePath = getFieldName(personPath, index, "verificationPhone");
  const extensionPath = getFieldName(personPath, index, "verificationPhoneExt");
  const extension = useController({ control, name: extensionPath });

  return (
    <FormField
      control={control}
      name={phonePath}
      render={({ field }) => (
        <FormItem>
          {/* Label on its own line; phone + extension + Call + Call Script on the
              next line at every breakpoint. FormMessage stays a stacked sibling
              below so it never renders inline. */}
          <div className="flex flex-col gap-2">
            <FormLabel className="whitespace-nowrap">
              {t("review.fields.verificationPhone")}
              <span className="text-destructive ml-0.5" aria-hidden="true">
                *
              </span>
            </FormLabel>
            {/* Phone + Extn share one row so neither moves when an error shows */}
            <div className="flex items-start gap-x-1">
              <FormControl>
                <Input {...field} type="text" className="w-44" value={field.value == null ? "" : String(field.value)} />
              </FormControl>
              <Input
                {...extension.field}
                type="text"
                aria-label={t("review.fields.verificationPhoneExt")}
                placeholder={t("review.fields.verificationPhoneExt")}
                className="w-20"
                value={extension.field.value == null ? "" : String(extension.field.value)}
              />
              {/* TODO(OR-21767): wire click-to-call / phone verification action */}
              <button
                type="button"
                aria-label={t("review.income.call")}
                className="text-success-foreground border-success-foreground hover:bg-success flex size-6 shrink-0 items-center justify-center rounded-md border transition-colors"
              >
                <PhoneIcon className="size-3.5 fill-current" aria-hidden="true" />
              </button>
              {/* TODO(OR-21767): open the Call Script modal */}
              <button
                type="button"
                className="bg-muted text-muted-foreground border-border hover:border-primary flex h-6 shrink-0 items-center justify-center whitespace-nowrap rounded-md border px-3 text-xs font-semibold transition-colors"
              >
                {t("review.income.callScript")}
              </button>
            </div>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// DifferentWorkLocation checkbox — rendered full-width outside the loop
// ---------------------------------------------------------------------------
interface DifferentWorkLocationFieldProperties {
  control: Control<SignersInput>;
  personPath: PersonPath;
  index: number;
}

function DifferentWorkLocationField({ control, personPath, index }: DifferentWorkLocationFieldProperties) {
  const { t } = useTranslation();
  const fieldPath = getFieldName(personPath, index, "differentWorkLocation");
  const { field, fieldState } = useController({
    control,
    name: fieldPath,
  });
  const errorId = `${fieldPath}-error`;

  return (
    <div className="col-span-full flex items-center gap-2">
      <Checkbox
        id={fieldPath}
        checked={Boolean(field.value)}
        onCheckedChange={(checked) => {
          field.onChange(checked === true);
        }}
        aria-describedby={fieldState.error ? errorId : undefined}
        aria-invalid={fieldState.error ? true : undefined}
      />
      <label htmlFor={fieldPath} className="cursor-pointer text-xs font-normal">
        {t("review.income.differentWorkLocation")}
      </label>
      {fieldState.error && (
        <p id={errorId} className="text-destructive text-2xs">
          {fieldState.error.message}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// IncomeBlock
// ---------------------------------------------------------------------------
export function IncomeBlock({
  control,
  personPath,
  index,
  onRemove,
  approvalId,
  applicantId,
  applicantSsnType,
}: IncomeBlockProperties) {
  const { t } = useTranslation();
  // Income Type options filtered by the applicant's SSN/ITIN status (a non-SSN
  // applicant may only pick Self-Employed / Unemployed / Cash Income).
  const incomeTypeOptions = getIncomeTypeOptions(applicantSsnType);
  const [isOpen, setIsOpen] = useState(true);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showTaxes, setShowTaxes] = useState(false);
  const ordinal = INCOME_ORDINAL_LABELS[index] ?? `${index + 1}th`;
  const blockId = `${personPath}-income-${index}-content`;
  const calculatorPanelId = `${personPath}-income-${index}-calculator`;
  const taxesPanelId = `${personPath}-income-${index}-taxes`;
  const employerInfoPanelId = `${personPath}-income-${index}-employer-info`;
  const isLocked = index === 0;

  // ZIP -> City/State autofill: fills City and State when the user enters a
  // complete ZIP (the BFF returns a single city). `zipStatus` lets the ZIP
  // field surface a not-found / error state instead of silently no-op-ing.
  const { status: zipStatus, invalid: zipInvalid } = useZipAutofill({
    zipPath: getFieldName(personPath, index, "zip"),
    cityPath: getFieldName(personPath, index, "city"),
    statePath: getFieldName(personPath, index, "state"),
  });

  // grossMonthlyPay controller — overwritten reactively (below) and on Calculate
  // with the period gross monthly income.
  const grossMonthlyPayPath = getFieldName(personPath, index, "grossMonthlyPay");
  const { field: grossMonthlyPayField } = useController({
    control,
    name: grossMonthlyPayPath,
  });
  const setGrossMonthlyPay = grossMonthlyPayField.onChange;

  // Pay Frequency is a single shared field (the calculator and the taxes row
  // bind to the same path and the same PAY_FREQUENCY_OPTIONS), so both stay in
  // sync and the factor reflects whichever control set it.
  const payFrequencyPath = getFieldName(personPath, index, "payFrequency");
  const { field: payFrequencyField } = useController({ control, name: payFrequencyPath });
  const setPayFrequency = payFrequencyField.onChange;
  const watchedPayPerPeriod = useWatch({ control, name: getFieldName(personPath, index, "payPerPeriod") });
  const watchedPayFrequency = useWatch({ control, name: payFrequencyPath });
  const watchedPayDate = useWatch({ control, name: getFieldName(personPath, index, "payDate") });
  const watchedGrossPayYtd = useWatch({ control, name: getFieldName(personPath, index, "grossPayYtd") });
  const watchedGrossNet = useWatch({ control, name: getFieldName(personPath, index, "grossNet") });

  // Show Taxes computes live from the raw Pay per Period (period) and Gross Pay
  // YTD (YTD), matching CAPS2 parity. payDate (when set) selects the SS rate year.
  // grossNet drives the Net-pay ×1.21 gross-up (OR-22309 Phase 3a).
  const perPeriodForTaxes = Number(watchedPayPerPeriod) || 0;
  const payTypeForTaxes: "Gross" | "Net" = watchedGrossNet === "Net" ? "Net" : "Gross";
  const taxesBreakdown =
    perPeriodForTaxes > 0
      ? computeTaxes(
          perPeriodForTaxes,
          Number(watchedGrossPayYtd) || 0,
          typeof watchedPayDate === "string" ? watchedPayDate : undefined,
          payTypeForTaxes
        )
      : null;

  // Default Pay Frequency to "Monthly" whenever it is empty (new sections and
  // hydrated incomes without a frequency alike). Runs through the shared field,
  // so the calculator and the taxes row both reflect it.
  useEffect(() => {
    if (!watchedPayFrequency) {
      setPayFrequency("Monthly" satisfies PayFrequency);
    }
  }, [watchedPayFrequency, setPayFrequency]);

  // Overwrite Gross Monthly Pay with (Pay per Period × net-multiplier) × frequency factor.
  // No-ops unless Pay per Period is a positive number and a frequency is selected,
  // so a manually entered Gross Monthly Pay is otherwise left alone.
  // When grossNet is "Net", gross up by NET_PAY_FACTOR (1.21) before applying the
  // frequency factor, matching the calculator's income-basis treatment (OR-22309 Phase 3a).
  function recomputeGrossMonthlyPay(perPeriodValue: unknown, frequencyValue: unknown) {
    const perPeriod = Number(perPeriodValue);
    if (!frequencyValue || !perPeriod || perPeriod <= 0) {
      return;
    }
    const factor = PAY_FREQUENCY_FACTOR[frequencyValue as PayFrequency];
    if (!factor) {
      return;
    }
    const netMultiplier = watchedGrossNet === "Net" ? NET_PAY_FACTOR : 1;
    setGrossMonthlyPay(Math.round(perPeriod * netMultiplier * factor * 100) / 100);
  }

  // Pay per Period recomputes on blur (not on every keystroke).
  function handlePayPerPeriodBlur() {
    recomputeGrossMonthlyPay(watchedPayPerPeriod, watchedPayFrequency);
  }

  // Recompute Gross Monthly Pay when Pay Frequency or Gross/Net changes (discrete
  // selects). Skip the initial mount so a hydrated Gross Monthly Pay isn't
  // overwritten on load — only user-driven changes trigger the recompute.
  // watchedGrossNet is included so flipping Gross↔Net immediately updates the
  // outer Gross Monthly Pay field, matching the live Show-Taxes panel behaviour.
  const didMountReference = useRef(false);
  useEffect(() => {
    if (!didMountReference.current) {
      didMountReference.current = true;
      return;
    }
    recomputeGrossMonthlyPay(watchedPayPerPeriod, watchedPayFrequency);
    // Intentionally keyed on frequency + grossNet (both are discrete selects),
    // not on payPerPeriod — per-period recomputes on blur to avoid firing on
    // every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedPayFrequency, watchedGrossNet]);

  function handleToggle() {
    setIsOpen((previous) => !previous);
  }

  function handleRemove(event: React.MouseEvent) {
    event.stopPropagation();
    onRemove?.();
  }

  function handleToggleCalculator() {
    setShowCalculator((previous) => {
      const next = !previous;
      // Opening the calculator hides the Pay/Frequency/Gross-Net + Show Taxes
      // row, so collapse the taxes panel too (its toggle is no longer visible).
      if (next) {
        setShowTaxes(false);
      }
      return next;
    });
  }

  function handleToggleTaxes() {
    setShowTaxes((previous) => !previous);
  }

  // Called by IncomeCalculatorPanel when Calculate succeeds.
  // Overwrites Gross Monthly Pay with the period gross monthly income
  // (Pay per Period × pay-frequency factor = monthlyIncomePeriod), matching
  // CAPS2 Step6. The Show Taxes panel then recomputes from that value.
  function handleCalculated(result: IncomeBreakdownResult) {
    grossMonthlyPayField.onChange(result.monthlyIncomePeriod);
  }

  // Render a single income field by name, looked up from the declarative config.
  function renderField(
    name: keyof IncomeInput,
    onBlur?: () => void,
    invalid?: boolean,
    optionsOverride?: ReadonlyArray<{ value: string; label: string }>
  ) {
    const config = INCOME_FIELDS.find((f) => f.name === name);
    if (!config) {
      return null;
    }
    return (
      <FieldRenderer
        config={config}
        control={control}
        personPath={personPath}
        index={index}
        onBlur={onBlur}
        invalid={invalid}
        optionsOverride={optionsOverride}
      />
    );
  }

  return (
    <div>
      {/* Block header */}
      <div className="bg-muted flex items-center gap-2 rounded-md px-3 py-2">
        <button
          type="button"
          aria-expanded={isOpen}
          aria-controls={blockId}
          onClick={handleToggle}
          className="flex flex-1 items-center justify-between gap-2 text-left text-sm font-medium"
        >
          <span>{t("review.income.blockTitle", { position: ordinal })}</span>
          <ChevronDownIcon
            className={cn(
              "text-muted-foreground size-4 shrink-0 transition-transform duration-200",
              isOpen ? "rotate-180" : "rotate-0"
            )}
            aria-hidden="true"
          />
        </button>
        {!isLocked && onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={handleRemove}
            aria-label={t("review.income.removeIncome", { position: ordinal })}
            className="text-destructive hover:text-destructive shrink-0"
          >
            <Trash2Icon className="size-4" aria-hidden="true" />
          </Button>
        )}
      </div>

      {/* Block content */}
      {isOpen && (
        <div id={blockId} className="flex flex-col gap-2 px-3 pb-4 pt-3">
          {/* Income Type — own row; options gated by the applicant's SSN/ITIN status */}
          <div className="grid grid-cols-1 items-start gap-x-3 gap-y-2 sm:grid-cols-2">
            {renderField("incomeType", undefined, undefined, incomeTypeOptions)}
          </div>

          {/* Employer Name | Occupation */}
          <div className="grid grid-cols-1 items-start gap-x-3 gap-y-2 sm:grid-cols-2">
            {renderField("employerName")}
            {renderField("occupation")}
          </div>

          {/* Address — Street No. (2) | Street Name (8) | Ste # (2) on a 12-col grid */}
          <fieldset className="flex flex-col gap-1">
            <legend className="text-foreground text-sm font-medium">{t("review.income.address")}</legend>
            <div className="grid grid-cols-1 items-start gap-x-3 gap-y-2 sm:grid-cols-12">
              <div className="sm:col-span-2">{renderField("streetNumber")}</div>
              <div className="sm:col-span-8">{renderField("streetName")}</div>
              <div className="sm:col-span-2">{renderField("suite")}</div>
            </div>
          </fieldset>

          {/* Zip | City | State — stacked on mobile; Zip+City share a row with
              State below at sm; full 3/5/4 single row at lg */}
          <div className="grid grid-cols-1 items-start gap-x-3 gap-y-2 sm:grid-cols-12">
            <div className="sm:col-span-6 lg:col-span-3">
              {renderField("zip", undefined, zipInvalid)}
              <ZipLookupMessage status={zipStatus} />
            </div>
            <div className="sm:col-span-6 lg:col-span-5">{renderField("city")}</div>
            <div className="sm:col-span-12 lg:col-span-4">{renderField("state")}</div>
          </div>

          {/* Employment Verification Phone + Extn (grouped so neither shifts on error) */}
          <PhoneExtensionField control={control} personPath={personPath} index={index} />

          {/* Different work location checkbox — full width */}
          <DifferentWorkLocationField control={control} personPath={personPath} index={index} />

          {/* Gross Monthly Pay | Hire Date | Show Calculator — side by side.
              Hire Date renders here only while the calculator is closed; when the
              calculator opens it moves into the panel as the first field (same RHF
              field, so the value persists across the toggle). */}
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-start">
            {renderField("grossMonthlyPay")}
            {/* When the calculator is open Hire Date moves into the panel; reserve
                its slot here (sm+) so the Show/Hide Calculator button stays put. */}
            {showCalculator ? <div aria-hidden="true" className="hidden w-32 sm:block" /> : renderField("hireDate")}
            {/* Show Calculator — an invisible label-height spacer (sm+) keeps the
                button level with the inputs and fixed there when a sibling field
                shows an error (the error grows below the inputs, not here). */}
            <div className="flex flex-col gap-2">
              <span aria-hidden="true" className="hidden h-3 sm:block">
                {" "}
              </span>
              {/* Show / Hide Calculator toggle */}
              <Button
                type="button"
                variant="outline"
                size="xs"
                className="px-1.5"
                aria-expanded={showCalculator}
                aria-controls={calculatorPanelId}
                onClick={handleToggleCalculator}
              >
                {showCalculator ? t("review.income.hideCalculator") : t("review.income.showCalculator")}
              </Button>
            </div>
          </div>

          {/* Income Calculator panel — inline collapsible */}
          {showCalculator && (
            <IncomeCalculatorPanel
              control={control}
              personPath={personPath}
              index={index}
              panelId={calculatorPanelId}
              onCalculated={handleCalculated}
              onPayPerPeriodBlur={handlePayPerPeriodBlur}
            />
          )}

          {/* Pay per Period | Pay Frequency | Gross/Net | Show Taxes — one row.
              Hidden while the calculator is open (it owns Pay Frequency / per-period
              already); field values persist in RHF and reappear when it closes. */}
          {!showCalculator && (
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-start">
              {renderField("payPerPeriod", handlePayPerPeriodBlur)}
              {renderField("payFrequency")}
              {renderField("grossNet")}
              {/* Show / Hide Taxes toggle — spacer keeps button level with inputs */}
              <div className="flex flex-col gap-2">
                <span aria-hidden="true" className="hidden h-3 sm:block" />
                <Button
                  type="button"
                  variant="link"
                  size="xs"
                  className="cursor-pointer px-0 underline"
                  aria-expanded={showTaxes}
                  aria-controls={taxesPanelId}
                  onClick={handleToggleTaxes}
                >
                  {showTaxes ? t("review.income.hideTaxes") : t("review.income.showTaxes")}
                </Button>
              </div>
            </div>
          )}

          {/* Taxes panel — inline collapsible; computed live from Gross Monthly Pay */}
          {showTaxes && <IncomeTaxesPanel panelId={taxesPanelId} taxes={taxesBreakdown} />}

          {/* Pull Purchased Employer Info — eligibility-gated button + results table */}
          <PullPurchasedEmployerInfo approvalId={approvalId} applicantId={applicantId} panelId={employerInfoPanelId} />

          {/* Additional Information — full width */}
          {renderField("additionalInformation")}
        </div>
      )}
    </div>
  );
}
